import { Collapse, ColumnTable, Table } from '@octano/global-ui';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import Legend from '../../../components/progression/parts/Legend';
import { getColor } from '../../../components/progression/utils';

import {
  CourseHistorySection,
  CourseStatusTypes,
  CourseValidatedHistorySection,
} from '../../../types/curricularAdvancementTypes';

type CourseStatusEnumType = {
  [key in CourseStatusTypes]: {
    color?: string;
    text: string;
  };
};

export const CourseStatus: CourseStatusEnumType = {
  E: { color: getColor('E'), text: 'enrolled' },
  A: { color: getColor('A'), text: 'approved' },
  P: { color: getColor('P'), text: 'pending' },
  R: { color: getColor('R'), text: 'failed' },
  V: { color: getColor('V'), text: 'validated' },
  VS: { color: getColor('VS'), text: 'validated' },
  VV: { color: getColor('VV'), text: 'validated' },
};

export interface ICourseHistoryAccordeonProps {
  courses: CourseValidatedHistorySection[];
  average?: string | number;
  title: string;
}

export default function CourseHistoryAccordeon({
  courses,
  average,
  title,
}: ICourseHistoryAccordeonProps) {
  const { t } = useTranslation();
  const hasSummary = average !== undefined;

  const columns: ColumnTable<Partial<CourseHistorySection>>[] = useMemo(
    () => [
      {
        columnName: 'courseShorting',
        headerText: t('curricularAdvancement.courseHistory.table.shortening'),
      },
      {
        columnName: 'courseName',
        headerText: t('curricularAdvancement.courseHistory.table.name'),
      },
      {
        columnName: 'courseCredits',
        headerText: t('curricularAdvancement.courseHistory.table.credits'),
      },
      {
        columnName: 'studentSectionStatus',
        headerText: t('curricularAdvancement.courseHistory.table.status'),
        width: '15%',
        cellFormat: ({ row: course, data, index }) => {
          if (hasSummary && data!.length - 1 === index)
            return (
              <div className="d-flex pl-5 ">
                <p className="m-auto">
                  <strong>
                    {t(
                      'curricularAdvancement.courseHistory.table.semesterAverage',
                    )}
                  </strong>
                </p>
              </div>
            );

          const courseLegend = course.studentSectionStatus
            ? CourseStatus[course.studentSectionStatus]
            : null;
          return (
            <div className="d-flex justify-content-start pl-5">
              <Row className="g-legend-progression d-flex">
                <Col xs={6} sm="auto" className="g-status-container pr-4 py-2">
                  {courseLegend && (
                    <Legend
                      label={t(
                        `curricularAdvancement.CourseStudies.status.${courseLegend.text}`,
                      )}
                      color={courseLegend.color}
                    />
                  )}
                </Col>
              </Row>
            </div>
          );
        },
      },
      {
        columnName: 'gradeAvg',
        headerText: t('curricularAdvancement.courseHistory.table.score'),
        cellFormat: ({ row: course, data, index }) => {
          if (hasSummary && data!.length - 1 === index) {
            return (
              <p className="m-auto">
                <strong>
                  {average ??
                    t('curricularAdvancement.courseHistory.table.noScore')}
                </strong>
              </p>
            );
          }

          return (
            <p>
              {course.grade ??
                t('curricularAdvancement.courseHistory.table.noScore')}
            </p>
          );
        },
      },
    ],
    [t, average, hasSummary],
  );

  return (
    <div className="pb-4">
      <Collapse
        headerStyles={{ backgroundColor: '#f7f7f7', height: 50 }}
        contentClassName="pt-4 text-center"
        renderTitle={() => (
          <p className="fs-18  fw-600 my-auto text-primary">{title}</p>
        )}
      >
        <Table
          columns={columns}
          data={hasSummary ? [...courses, {}] : courses}
          striped
        />
      </Collapse>
    </div>
  );
}
